import React, { useMemo, useCallback } from 'react'
import { withRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Row, Col, Select, DatePicker, Spin } from 'antd'
import { useAiCreditCredits, useAiCreditData, useCreditUsage } from './hooks'
import OrganizationUsage from './components/OrganizationUsage'
import UsageByService from './components/UsageByService'
import TopCreditUsers from './components/TopCreditUsers'
import CreditUsageTable from './components/CreditUsageTable'
import ErrorState from './components/ErrorState'
import './AiCreditUsageReport.scss'
import NewHeader from '../../components/NewHeader'
import ChevronRightIcon from '../../assets/icons/chevron-right-icon.svg'
import ReportsIcon from '../../assets/icons/reports-icon.svg'
import moment from 'moment'

const { Option } = Select
const { RangePicker } = DatePicker

const AiCreditUsageReport = ({ history }) => {
  const { t } = useTranslation()

  // Date filter, search, and user data management for Credits
  const {
    tableFilter: creditsTableFilter,
    setTableFilter: setCreditsTableFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    creditsData,
    creditsLoading,
    creditsError,
    creditsPagination,
    refetchCredits,
    creditsSearchTerm,
    onCreditsSearch,
    onCreditsPageChange,
    setDateRange
  } = useAiCreditCredits('5')

  // Course Credit Usage data
  const {
    data: courseUsageData,
    loading: courseUsageLoading,
    error: courseUsageError,
    pagination: courseUsagePagination,
    searchTerm: courseUsageSearchTerm,
    onSearch: onCourseUsageSearch,
    onPageChange: onCourseUsagePageChange,
    setTableFilter: setCourseUsageTableFilter,
    setDateRange: setCourseUsageDateRange,
    tableFilter: courseUsageTableFilter,
    dateFrom: courseUsageDateFrom,
    dateTo: courseUsageDateTo,
    refetch: refetchCourseUsage,
    selectedType: courseUsageSelectedType,
    onTypeChange: onCourseUsageTypeChange
  } = useCreditUsage('5')

  // Organization-level credit data
  const {
    data: creditData,
    loading: creditLoading,
    error: creditError,
    refetch: refetchOrgCredits,
    tableFilter: orgTableFilter,
    setTableFilter: setOrgTableFilter
  } = useAiCreditData()

  // Sync org filters to credits table filters
  React.useEffect(() => {
    if (setOrgTableFilter && creditsTableFilter) {
      setOrgTableFilter(creditsTableFilter)
    }
    if (setCourseUsageTableFilter && creditsTableFilter) {
      setCourseUsageTableFilter(creditsTableFilter)
    }
    if (setDateRange) {
      const dates = [dateFrom ? moment(dateFrom) : null, dateTo ? moment(dateTo) : null]
      const strings = [dateFrom || '', dateTo || '']
      setDateRange(dates, strings)
    }
    if (setCourseUsageDateRange) {
      const dates = [dateFrom ? moment(dateFrom) : null, dateTo ? moment(dateTo) : null]
      const strings = [dateFrom || '', dateTo || '']
      setCourseUsageDateRange(dates, strings)
    }
  }, [creditsTableFilter, dateFrom, dateTo, setOrgTableFilter, setDateRange, setCourseUsageTableFilter, setCourseUsageDateRange])

  const loading = creditLoading || creditsLoading || courseUsageLoading
  const error = creditError || creditsError || courseUsageError

  const isTableActionLoading = React.useRef(false)

  React.useEffect(() => {
    if (!creditsLoading) {
      isTableActionLoading.current = false
    }
  }, [creditsLoading])

  const data = useMemo(() => {
    if (!creditData) return null
    return {
      ...creditData,
      topUsers: creditsData
    }
  }, [creditData, creditsData])

  const refetch = useCallback(() => {
    refetchOrgCredits()
    refetchCredits()
    refetchCourseUsage()
  }, [refetchOrgCredits, refetchCredits, refetchCourseUsage])

  const handleCreditsExportClick = useCallback(() => {
    if (!creditsData || creditsData.length === 0) return

    const header = ['Rank', 'User', 'Credits Used']
    const rows = creditsData.map((row, index) => {
      return [
        row.rank || (creditsPagination?.current - 1) * creditsPagination?.pageSize + index + 1,
        row.name || '',
        row.totalCreditsUsed || 0
      ]
    })

    const csvContent = [header, ...rows]
      .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const fileName = `ai_credit_usage_${moment().format('YYYY-MM-DD')}.csv`

    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [creditsData, creditsPagination])

  const handleRetry = useMemo(
    () => () => {
      refetch()
    },
    [refetch]
  )

  // Determine view state
  const viewState = useMemo(() => {
    if (loading && (!data || (!creditData && !creditsData))) return 'loading'
    if (error && !data) return 'error'
    return 'success'
  }, [creditData, creditsData, error, data, loading])

  // Render based on state
  const renderContent = () => {
    if (viewState === 'loading' && !isTableActionLoading.current) {
      return (
        <div className="ai-credit-loading-container">
          <Spin size="large" />
        </div>
      )
    }
    if (viewState === 'error') {
      return <ErrorState error={error} onRetry={handleRetry} />
    }

    return (
      <>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div>
              <h3 className="org-title">Organization Usage</h3>
            </div>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <OrganizationUsage
              data={data}
              loading={creditLoading}
              onPurchaseClick={() => console.log('Purchase clicked')}
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={12}>
            <UsageByService data={data} loading={creditLoading} />
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <TopCreditUsers
              title="Top AI Credit Users"
              valueTitle="Credits Users"
              isCreditsTable={true}
              data={data ? { ...data, topUsers: creditsData || [] } : { topUsers: [] }}
              loading={creditsLoading}
              onExportClick={handleCreditsExportClick}
              onSearch={(term) => {
                isTableActionLoading.current = true
                onCreditsSearch(term)
              }}
              searchTerm={creditsSearchTerm}
              pagination={creditsPagination}
              onPageChange={(page, pageSize) => {
                isTableActionLoading.current = true
                onCreditsPageChange(page, pageSize)
              }}
              tableFilter={creditsTableFilter}
              onTableFilterChange={setCreditsTableFilter}
            />
          </Col>
        </Row>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <CreditUsageTable
              data={courseUsageData}
              loading={courseUsageLoading}
              searchTerm={courseUsageSearchTerm}
              onSearch={(term) => {
                isTableActionLoading.current = true
                onCourseUsageSearch(term)
              }}
              pagination={courseUsagePagination}
              onPageChange={(page, pageSize) => {
                isTableActionLoading.current = true
                onCourseUsagePageChange(page, pageSize)
              }}
              activeTypes={['course', 'ai-image', 'ai_video', 'audio', 'podcast', 'maze', 'sharebot']}
              selectedType={courseUsageSelectedType}
              onTypeChange={onCourseUsageTypeChange}
            />
          </Col>
        </Row>
      </>
    )
  }

  const goBack = () => {
    history.push('/reports')
  }
  return (
    <div className="ai-credit-usage-report">
      <div className="ai-credit-nav-header">
        <div className="ai-credit-usage-report-breadcrumbs">
          <button className="breadcrumb-home" onClick={() => goBack()}>
            <img src={ReportsIcon} alt="Home" width="20" height="20" />
            Reports
          </button>
          <span className="breadcrumb-sep">
            <img src={ChevronRightIcon} alt=">" width="24" height="24" />
          </span>
          <span className="breadcrumb-settings">
            Ai Credits Usage
          </span>
        </div>
        <div className="ai-credit-usage-report-newheader">
          <NewHeader />
        </div>
      </div>
      <div className="report-header">
        <div className="header-content">
          <div className="title-section">
            <h1 className="main-title">AI Credits Usage</h1>
            <p className="subtitle">
              Real-time visibility into AI credits usage at the organization level
            </p>
          </div>
          <div className="filters-section" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {creditsTableFilter === 'Other' && (
              <RangePicker
                size="medium"
                format="YYYY-MM-DD"
                placeholder={[t("reports:from_date"), t("reports:to_date")]}
                showTime={false}
                value={[dateFrom ? moment(dateFrom) : null, dateTo ? moment(dateTo) : null]}
                onChange={(dates, dateStrings) => {
                  isTableActionLoading.current = false
                  setDateFrom(dateStrings[0])
                  setDateTo(dateStrings[1])
                }}
              />
            )}
            <Select
              className="time-filter-select"
              size="large"
              value={creditsTableFilter}
              onChange={(value) => {
                isTableActionLoading.current = false
                setCreditsTableFilter(value)
                if (value !== 'Other') {
                  setDateFrom('')
                  setDateTo('')
                }
              }}
              style={{ width: 150 }}
            >
              <Option value="All Time">{t('reports:all_time')}</Option>
              <Option value="Today">{t('reports:today')}</Option>
              <Option value="This Week">{t('reports:seven_days')}</Option>
              <Option value="This Month">{t('reports:last_month')}</Option>
              <Option value="This Year">{t('reports:one_year')}</Option>
              <Option value="Other">{t('v4:others')}</Option>
            </Select>
          </div>
        </div>
      </div>

      <div className="report-body">
        {renderContent()}
      </div>
    </div>
  )
}
export default withRouter(AiCreditUsageReport)
